import { resolveUploadAssetUrl } from '../../lib/resolveUploadAssetUrl';
import { stripHtml } from '../../lib/editorAi';
import { parseSupportBody } from '../../lib/supportConstants';
import { formatFileSize, fileExtensionFrom, isDirectVideoUrl } from '../documents/blocks/blockUtils';

function resolveHtmlAssetUrls(html) {
  if (!html) return '';
  return html.replace(/(\s(?:src|href)=)(["'])(.*?)\2/gi, (match, prefix, quote, url) => {
    return `${prefix}${quote}${resolveUploadAssetUrl(url)}${quote}`;
  });
}

function LegacyEditorJsRenderer({ blocks }) {
  const renderBlock = (block, index) => {
    const { type, data } = block;
    switch (type) {
      case 'paragraph':
        return (
          <p
            key={index}
            className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data?.text || '' }}
          />
        );
      case 'header': {
        const level = Math.min(6, Math.max(1, data?.level || 2));
        const Tag = `h${level}`;
        return (
          <Tag
            key={index}
            className="font-semibold font-[Manrope] text-slate-900 dark:text-white text-base mt-2 mb-1"
            dangerouslySetInnerHTML={{ __html: data?.text || '' }}
          />
        );
      }
      case 'image': {
        const url = resolveUploadAssetUrl(data?.url);
        if (!url) return null;
        return (
          <figure key={index} className="my-3">
            <img src={url} alt="" className="rounded-lg max-w-full max-h-80 object-contain border border-slate-200 dark:border-slate-700" />
          </figure>
        );
      }
      case 'video': {
        const url = resolveUploadAssetUrl(data?.url);
        if (!url) return null;
        if (isDirectVideoUrl(url)) {
          return <video key={index} src={url} controls className="rounded-lg max-w-full max-h-80 my-3" />;
        }
        return (
          <a key={index} href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600">
            {data?.caption || url}
          </a>
        );
      }
      case 'file': {
        const url = resolveUploadAssetUrl(data?.url);
        if (!url) return null;
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="my-2 flex items-center gap-2 text-sm text-blue-600 hover:underline"
          >
            {data?.filename || 'Download file'}
            {data?.size ? ` (${formatFileSize(data.size)})` : ''}
          </a>
        );
      }
      case 'quote':
        return (
          <blockquote key={index} className="border-l-4 border-slate-300 pl-4 my-2 text-sm italic text-slate-600">
            <div dangerouslySetInnerHTML={{ __html: data?.text || '' }} />
          </blockquote>
        );
      case 'code':
        return (
          <pre key={index} className="my-2 rounded bg-slate-900 text-slate-100 p-3 text-xs overflow-x-auto">
            <code>{data?.code || ''}</code>
          </pre>
        );
      default:
        if (data?.text) {
          return (
            <p key={index} className="text-sm" dangerouslySetInnerHTML={{ __html: data.text }} />
          );
        }
        return null;
    }
  };

  if (!blocks?.length) {
    return <p className="text-sm text-slate-500 italic">No content</p>;
  }
  return <div className="space-y-1">{blocks.map(renderBlock)}</div>;
}

export default function SupportMessageRenderer({ body, className = '' }) {
  const parsed = parseSupportBody(body);

  if (parsed.format === 'quill') {
    const html = resolveHtmlAssetUrls(parsed.html || '');
    const plain = stripHtml(html).trim();
    if (!plain && !/<(img|video|a)\b/i.test(html)) {
      return <p className="text-sm text-slate-500 italic">No content</p>;
    }
    return (
      <div
        className={`support-quill-content text-sm text-slate-700 dark:text-slate-300 leading-relaxed ${className}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return <LegacyEditorJsRenderer blocks={parsed.blocks} />;
}
